import type { Tutorial, TutorialVersion } from '@platform/shared-types';

// Self-contained client for academy-ext-service -- the tutorial recorder:
// step storage, speech-to-text, narration cleanup and the versioned render
// pipeline behind the in-app tutorial player.
//
// Mirrors academyApi.ts's conventions exactly -- same shared `platform`
// cluster host resolution, same auth header source, same error-surfacing.
//
// NOTE the host shape: this service lives on the SHARED `platform` cluster
// and is addressed by BASE PATH (/academy-ext), never a per-service
// subdomain. Do not "correct" this to academy-ext.api.<apex> -- that pattern
// is closed (the account is at the 120 EDGE API Gateway cap).

/** Exported for the Library tab's bucket name (agenticpos-media-{stage}). */
export const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver -- exported for tests. */
export function resolveTutorialHost(
  hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string,
): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/academy-ext-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/academy-ext`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/academy-ext`;
  return `https://platform.api.dev.${apex}/academy-ext`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_ACADEMY_EXT_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveTutorialHost(h, detectStage(), envBase);
};

// Same auth source as academyApi.ts / hardwareApi.ts / billingApi.ts /
// deviceConfigApi.ts: super-admin-ui stores the admin session token under
// 'jwtToken' and sends it bare (no "Bearer " prefix). platform-app ALSO
// stores its session under the key 'jwtToken' (services/apiService.ts does
// `localStorage.setItem('jwtToken', session.idToken)`) -- the KEY is the
// same in both apps; only the underlying Cognito token differs by pool.
// There is no separate "platform-app idToken key" to avoid here.
function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

/**
 * Thrown by `call()` on a non-2xx response. `blockers` is populated only for
 * the publish gate's 422 -- `{ error, blockers: [{stepId, order, reason}] }`
 * -- so a caller runs `describeBlockers(e.blockers)` instead of parsing
 * anything out of the message string.
 */
export class TutorialApiError extends Error {
  blockers?: Array<{ stepId: string; order: number; reason: string }>;
  constructor(message: string, blockers?: TutorialApiError['blockers']) {
    super(message);
    this.name = 'TutorialApiError';
    this.blockers = blockers;
  }
}

/**
 * `tenantId`, when given, is sent as `x-tenant-id` on this one request only --
 * per CLAUDE.md, `custom:tenantId` is a comma list and the header SELECTS
 * from it, never adds. A super admin may name any tenant or omit it entirely
 * to act globally; every tutorialApi method below takes it as its own last
 * argument rather than a client-wide setting, so a caller can address a
 * different tenant per call (e.g. list everything, then create one tutorial
 * scoped to a specific merchant) without any shared mutable state.
 */
export async function tutorialCall<T>(path: string, init: RequestInit = {}, tenantId?: string | null): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (tenantId) headers['x-tenant-id'] = tenantId;

  const res = await fetch(`${host()}${path}`, { ...init, headers });
  if (!res.ok) {
    // Surface the server's own message -- the publish gate's blockers are the
    // most useful thing a user can see here.
    let detail = '';
    let blockers: TutorialApiError['blockers'];
    try {
      const body = await res.json();
      detail = body?.error || '';
      if (Array.isArray(body?.blockers)) blockers = body.blockers;
    } catch { /* non-JSON body */ }
    throw new TutorialApiError(detail || `academy-ext ${res.status}`, blockers);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

// Kept so every existing call site below reads unchanged; videoTasksApi.ts
// imports `tutorialCall` directly so both clients share one host/auth/error path.
const call = tutorialCall;

export interface TutorialWithVersions { tutorial: Tutorial; versions: TutorialVersion[] }

export const tutorialApi = {
  list: (tenantId?: string) =>
    call<{ tutorials: Tutorial[] }>('/admin/tutorials', {}, tenantId),

  create: (
    input: { slug: string; title: string; surface?: 'platform-app' | 'pos' },
    tenantId?: string,
  ) => call<{ tutorial: Tutorial }>(
    '/admin/tutorials', { method: 'POST', body: JSON.stringify(input) }, tenantId,
  ),

  get: (tutorialId: string, tenantId?: string) =>
    call<TutorialWithVersions>(`/admin/tutorials/${encodeURIComponent(tutorialId)}`, {}, tenantId),

  getDraft: (tutorialId: string, tenantId?: string) =>
    call<{ draft: TutorialVersion }>(
      `/admin/tutorials/${encodeURIComponent(tutorialId)}/draft`, {}, tenantId,
    ),

  saveDraft: (tutorialId: string, steps: TutorialVersion['steps'], tenantId?: string) =>
    call<{ draft: TutorialVersion }>(
      `/admin/tutorials/${encodeURIComponent(tutorialId)}/draft`,
      { method: 'PUT', body: JSON.stringify({ steps }) },
      tenantId,
    ),

  transcribe: (
    tutorialId: string,
    input: { audioUri: string; clickMs: number[]; phraseHints?: string[] },
    tenantId?: string,
  ) => call<{ draft: TutorialVersion }>(
    `/admin/tutorials/${encodeURIComponent(tutorialId)}/transcribe`,
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  ),

  clean: (tutorialId: string, tenantId?: string) =>
    call<{ draft: TutorialVersion }>(
      `/admin/tutorials/${encodeURIComponent(tutorialId)}/narration/clean`,
      { method: 'POST' },
      tenantId,
    ),

  render: (tutorialId: string, versionNo?: number, tenantId?: string) =>
    call<{ render: TutorialVersion['render'] }>(
      `/admin/tutorials/${encodeURIComponent(tutorialId)}/render`,
      { method: 'POST', body: JSON.stringify(versionNo ? { versionNo } : {}) },
      tenantId,
    ),

  publish: (tutorialId: string, tenantId?: string) =>
    call<{ tutorial: Tutorial; version: TutorialVersion }>(
      `/admin/tutorials/${encodeURIComponent(tutorialId)}/publish`,
      { method: 'POST' },
      tenantId,
    ),

  rollback: (tutorialId: string, versionNo: number, tenantId?: string) =>
    call<{ tutorial: Tutorial; version: TutorialVersion }>(
      `/admin/tutorials/${encodeURIComponent(tutorialId)}/rollback`,
      { method: 'POST', body: JSON.stringify({ versionNo }) },
      tenantId,
    ),

  /** Retires -- never deletes. See tutorials.remove on the service side. */
  retire: (tutorialId: string, tenantId?: string) =>
    call<{ tutorial: Tutorial }>(
      `/admin/tutorials/${encodeURIComponent(tutorialId)}`, { method: 'DELETE' }, tenantId,
    ),

  presignClip: (
    tutorialId: string,
    input: { kind: 'session' | 'step'; stepId?: string; contentType: string; sizeBytes: number },
    tenantId?: string,
  ) => call<{
    upload: { url: string; fields: Record<string, string> };
    clip: { key: string; cdnUrl: string; contentType: string; sizeBytes: number };
    limits: { maxBytes: number; allowedTypes: readonly string[] };
  }>(
    `/admin/tutorials/${encodeURIComponent(tutorialId)}/clips/upload-url`,
    { method: 'POST', body: JSON.stringify(input) },
    tenantId,
  ),

  /**
   * Uploads straight to S3 via a presigned POST -- same reasoning as
   * academyApi.uploadVideo: API Gateway caps a request body at 10 MB and a
   * screen recording is routinely far larger. XHR (not fetch) purely because
   * only XHR reports upload progress.
   */
  uploadClip: (
    file: File | Blob,
    presigned: { url: string; fields: Record<string, string> },
    onProgress?: (percent: number) => void,
  ) => new Promise<void>((resolve, reject) => {
    const form = new FormData();
    Object.entries(presigned.fields).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', presigned.url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('upload failed'));
    xhr.send(form);
  }),
};

// ---------------------------------------------------------------------------
// CDN URL helpers -- for the "live manifest URL" (never changes across a
// publish/rollback) and per-step clip thumbnails.
//
// Mirrors services/shared/academy-ext-service/src/lib/clipUpload.ts
// (tenantSegment / manifestKey / cdnUrlFor) exactly. There is no shared
// package these can come from -- that file lives inside a backend service,
// not @platform/shared-types -- so the same tiny sanitisation/segment rules
// are duplicated here on purpose. Keep both in sync if either changes.
// ---------------------------------------------------------------------------

/** Same host-detection shape as `host()` above, but for the media CDN, not the API. */
export function resolveTutorialCdnHost(hostname: string, stage: 'prod' | 'staging' | 'dev'): string {
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `image.${apex}`;
  if (stage === 'staging') return `image.stage.${apex}`;
  return `image.dev.${apex}`;
}

const safeSegment = (s: string) => (s || '').replace(/[^A-Za-z0-9_-]/g, '');
const tenantSegment = (tenantId: string | null) => (tenantId ? safeSegment(tenantId) : 'global');

export function tutorialCdnUrl(key: string): string {
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return `https://${resolveTutorialCdnHost(h, detectStage())}/${key}`;
}

/**
 * The public manifest URL for one tutorial -- what a landing page or the
 * in-app player embeds. It never changes across a publish, a re-render or a
 * rollback; only its JSON content is repointed server-side.
 */
export function manifestUrl(tenantId: string | null, slug: string): string {
  return tutorialCdnUrl(`tutorials/${tenantSegment(tenantId)}/${safeSegment(slug)}/manifest.json`);
}

/**
 * Where to read a step's clip from, for a thumbnail capture. A `slice` clip
 * points at the WHOLE shared session recording, so the caller must seek to
 * `seekSeconds` before the frame at `currentTime` means anything for this
 * particular step; a `standalone` re-record is already exactly this step, so
 * `seekSeconds` is 0.
 */
export function stepClipSource(
  clip: TutorialVersion['steps'][number]['clip'],
): { url: string; seekSeconds: number } | null {
  if (!clip) return null;
  if (clip.kind === 'standalone') return { url: tutorialCdnUrl(clip.key), seekSeconds: 0 };
  return { url: tutorialCdnUrl(clip.sourceKey), seekSeconds: clip.startMs / 1000 };
}
